import { AnimationContextProps } from '../types';

export enum UnitsPreference {
    imperial = 'imperial',
    metric = 'metric'
}

export interface ActivityMapContainerProps
    extends ActivityMapOrChartContainerProps {
    showNavigationControls: boolean;
    /** Percentage of the map width to offset the path to the right. */
    offsetRightPercent?: number;
    /** Map width in pixels below which the path will not be offset to the right */
    offsetRightBreakpoint?: number;
}

export interface ActivityStreamProps {
    id?: string;
    resolution: StravaResolution;
    xAxisStreamTypes: (StravaStreamType.distance | StravaStreamType.time)[];
    yAxisStreamType: StravaStreamType | CustomStreamType | null;
}

export type ActivityMapOrChartContainerProps = {
    autoplayAnimation: boolean;
    color: string;
    unitsPreference: UnitsPreference;
    activity?: Activity;
    loading?: boolean;
    error?: any;
} & ActivityStreamProps;

export interface ActivityChartContainerProps
    extends ActivityMapOrChartContainerProps {
    name: string;
    style?: React.CSSProperties;
    height: number;
    isSelected: boolean;
    isTopItem: boolean;
    isBottomItem: boolean;
}

export interface ActivityMapOrChartProps {
    id?: string;
    color: string;
    error?: any;
    loading?: boolean;
    unitsPreference: UnitsPreference;
    xAxisStreams: ScalarStream[];
    yAxisStream?: ScalarStream;
    autoplayAnimation?: boolean;
}

export interface ActivityMapProps extends ActivityMapOrChartProps {
    /** Indicates whether direction should be animated when in flyby mode */
    animateBearing?: boolean;

    /** Starting bearing for the map */
    mapBearing?: number;

    lngLatData: number[][];
    /** Map width in pixels below which the path will not be offset to the right */
    offsetRightBreakpoint?: number;

    /** Percentage of the map width to offset the path to the right. */
    offsetRightPercent?: number;

    /** Percentage of the map height to offset the path downwards, when below the breakpoint */
    offsetBottomPercent?: number;

    showNavigationControls: boolean;
}

export interface ActivityChartProps extends ActivityMapOrChartProps {
    height: number;
    isSelected: boolean;
}

export interface SelectedIndexOptions {
    selectedIndex: number;
    selectedIndexOffset: number;
}

export type AnimatedSelectedIndexProps = AnimationContextProps &
    SelectedIndexOptions & {
        onSelectedIndexChanged: (options: SelectedIndexOptions) => void;
    };

export interface Activity {
    id?: string;
    name?: string;
    distance?: number;
    demoActivity?: boolean;
    elapsed_time?: number;
    average_speed?: number;
    googleMapsUrl?: string;
    mapboxUrl?: string;
    mapBearing?: number;
    location?: string;
    summaryPolyline?: string;
    start_latlng?: number[];
    type?: string;
    timeSinceActivityInWords?: string;
    __typename?: string;
    lngLatStream: { data: number[][] };
    scalarStreams?: ScalarStream[];
    stats?: {
        distance: {
            units: string;
            value: number;
        };
        elapsedTime: {
            units: string;
            value: number;
        };
        averageSpeed: {
            units: string;
            value: number;
        };
        averagePace: {
            units: string;
            value: number;
        };
    };
}

export interface ScalarStream {
    id: string;
    activity_id: string;
    type: StravaStreamType | CustomStreamType;
    friendlyName: string;
    data: number[];
    isEmpty: boolean;
    units: string;
    resolution: StravaResolution;
}

export interface ChartSeriesStream extends ScalarStream {
    offsetForMap: number;
    formatForDisplay: (value: number) => string;
    transformedData: (number | null)[];
    formatTransformedDataPoint: (value: number | null) => string;
    transformedBounds: { min: number; max: number; avg: number };
}

interface ActivityStreamAthletePayload {
    __typename?: 'Athlete';
    activityStreamTypes: (StravaStreamType | CustomStreamType)[];
    autoplayAnimation?: boolean;
    id: string;
    resolution?: StravaResolution;
    selectedActivityStreamType: StravaStreamType | CustomStreamType | null;
    unitsPreference?: UnitsPreference;
}

export interface SelectActivityStreamProps {
    selectActivityStream?: (
        streamType?: StravaStreamType | CustomStreamType
    ) => void;
}

export enum MoveDirection {
    up = 'up',
    down = 'down'
}

export type SaveAthleteOptionsProps = AddActivityStreamProps &
    MoveActivityStreamProps &
    RemoveActivityStreamProps &
    SelectActivityStreamProps & {
        toggleAutoplayAnimation?: (autoplay?: boolean) => void;
        updateUnitsPreference?: (unitsPreference: UnitsPreference) => void;
    };

export interface MoveActivityStreamProps {
    moveActivityStream?: (
        streamType: StravaStreamType | CustomStreamType,
        direction: MoveDirection
    ) => void;
}

export interface AthleteOptions {
    activityStreamTypes: (StravaStreamType | CustomStreamType)[];
    autoplayAnimation?: boolean;
    resolution?: StravaResolution;
    selectedActivityStreamType: StravaStreamType | CustomStreamType | null;
    unitsPreference?: UnitsPreference;
}

export interface AthleteOptionsPayload {
    __typename?: 'Mutation';
    athleteOptions: {
        __typename?: 'AthleteOptionsPayload';
        athlete: ActivityStreamAthletePayload;
    };
}

export interface RemoveActivityStreamProps {
    removeActivityStream?: (
        streamType: StravaStreamType | CustomStreamType
    ) => void;
}

export interface AddActivityStreamProps {
    addActivityStream?: (
        streamType: StravaStreamType | CustomStreamType,
        fetchStreamForActivityId?: string
    ) => Promise<any>;
}

export interface ActivityStreamInfo {
    type: StravaStreamType | CustomStreamType;
    friendlyName: string;
    units: string;
}

export interface ActivityStream<TData extends number | number[] | boolean> {
    id: string;
    type: StravaStreamType | CustomStreamType;
    friendlyName: string;
    data: TData[];
}

export enum StravaStreamType {
    time = 'time',
    distance = 'distance',
    latlng = 'latlng',
    altitude = 'altitude',
    velocity_smooth = 'velocity_smooth',
    heartrate = 'heartrate',
    cadence = 'cadence',
    watts = 'watts',
    temp = 'temp',
    moving = 'moving',
    grade_smooth = 'grade_smooth'
}

export enum CustomStreamType {
    pace = 'pace',
    empty = 'empty'
}

export interface CustomStream {
    id: string;
    type: CustomStreamType;
    data: number[];
    resolution: StravaResolution;
    friendlyName: string;
    units: string;
}

export enum StravaResolution {
    low = 'low',
    medium = 'medium',
    high = 'high'
}

export interface ActivityStreamInput {
    id: string;
    selectedStreamType: StravaStreamType | CustomStreamType | null;
    types: (StravaStreamType | CustomStreamType)[];
    resolution?: StravaResolution;
}

export interface ActivityMapOrChartVariables {
    input: { id: string };
    resolution: StravaResolution;
    streamTypes: (StravaStreamType | CustomStreamType)[];
}

export interface FrontPageActivityMapVariables {
    streamTypes: (StravaStreamType | CustomStreamType)[];
}

export interface ActivityMapOrChartPayload {
    activity: ActivityPayload;
}

export interface Athlete {
    activityStreamTypes: (StravaStreamType | CustomStreamType)[];
    autoplayAnimation?: boolean;
    id?: string;
    measurement_preference?: string;
    profile?: string;
    resolution?: StravaResolution;
    selectedActivityStreamType: StravaStreamType | CustomStreamType | null;
    unitsPreference?: UnitsPreference;
}

export interface ActivityListProps {
    activities?: ActivityPayload[];
    error?: any;
    loading?: boolean;
    handleFetchMore?: (page: number) => void;
    prefetchActivity?: (id: string) => void;
    pageInfo?: {
        page: number;
        pageSize: number;
        hasMore: boolean;
    };
}

export interface ActivityInput {
    id: string;
}

export interface ActivityStreamsInput {
    id: string;
    selectedStreamType: StravaStreamType | CustomStreamType;
    activityStreamTypes: (StravaStreamType | CustomStreamType)[];
}

export interface ActivityPayload {
    id?: string;
    activity: Activity;
    loading: boolean;
    error?: any;
}

export interface ActivityProps {
    activity?: Activity;
    error?: any;
    loading?: boolean;
}

export interface AthletePayload extends ActivityStreamInfoPayload {
    loggedInUser?: {
        user?: {
            id: any;
            username: string;
            name: string;
            isAdministrator: boolean;
            athlete?: Athlete;
            appStatus?: string;
        };
    };
}

export interface ActivityStreamInfoPayload {
    activityStreamInfoList?: {
        id?: string;
        activityStreamInfoList?: ActivityStreamInfo[];
    };
}

export interface AthleteProps extends AthletePayload {
    error?: any;
    loading?: boolean;
}

export interface ActivityListInput {
    page?: number;
    pageSize?: number;
}

export interface ActivityListPayload {
    activityList: {
        activities?: ActivityPayload[];
    };
}

export interface DemoActivityListPayload {
    demoActivityList: {
        activities?: ActivityPayload[];
    };
}



// WEBPACK FOOTER //
// ./src/client/components/Strava/types.ts